/**
 * Google Calendar Integration Service
 * Handles Google Calendar connection and management
 */
class GoogleCalendarService {
  static API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  /**
   * Get Google Calendar authorization URL
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Response containing auth URL
   */
  static async getAuthUrl(token) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/google-calendar/auth/url`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting Google Calendar auth URL:', error);
      throw error;
    }
  }

  /**
   * Get Google Calendar connection status
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Response containing connection status
   */
  static async getConnectionStatus(token) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/google-calendar/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting Google Calendar status:', error);
      throw error;
    }
  }

  /**
   * Test Google Calendar connection
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Response containing test results
   */
  static async testConnection(token) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/google-calendar/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error testing Google Calendar connection:', error);
      throw error;
    }
  }

  /**
   * Disconnect Google Calendar
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Response containing disconnect result
   */
  static async disconnect(token) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/google-calendar/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      throw error;
    }
  }

  /**
   * Update Google Calendar settings
   * @param {Object} settings - Settings to update
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Response containing updated settings
   */
  static async updateSettings(settings, token) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/google-calendar/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating Google Calendar settings:', error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback (used when redirecting back from Google)
   * This is typically handled by the backend, but we can check for URL parameters
   * @returns {Object|null} Callback parameters if present
   */
  static handleOAuthCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const calendarConnected = urlParams.get('calendar_connected');
    const calendarError = urlParams.get('calendar_error');

    if (code || error || calendarConnected || calendarError) {
      return {
        code,
        state,
        error,
        calendarConnected: calendarConnected === 'true',
        calendarError: calendarError === 'true'
      };
    }

    return null;
  }

  /**
   * Clean OAuth parameters from URL
   */
  static cleanOAuthParams() {
    const url = new URL(window.location);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    url.searchParams.delete('calendar_connected');
    url.searchParams.delete('calendar_error');
    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Open Google Calendar authorization in a popup window
   * @param {string} authUrl - Authorization URL
   * @returns {Promise<boolean>} Promise that resolves when auth is complete
   */
  static openAuthPopup(authUrl) {
    return new Promise((resolve, reject) => {
      const popup = window.open(
        authUrl,
        'google-calendar-auth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        reject(new Error('Could not open popup window. Please allow popups for this site.'));
        return;
      }

      // Check popup status periodically
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          // Give a moment for the callback to process
          setTimeout(() => {
            const callback = this.handleOAuthCallback();
            if (callback && (callback.calendarConnected || callback.code)) {
              this.cleanOAuthParams();
              resolve(true);
            } else if (callback && (callback.error || callback.calendarError)) {
              this.cleanOAuthParams();
              reject(new Error('Calendar authorization failed'));
            } else {
              resolve(false); // User cancelled
            }
          }, 100);
        }
      }, 1000);

      // Timeout after 10 minutes
      setTimeout(() => {
        if (!popup.closed) {
          popup.close();
          clearInterval(checkClosed);
          reject(new Error('Authorization timeout'));
        }
      }, 10 * 60 * 1000);
    });
  }

  /**
   * Get default calendar settings
   * @returns {Object} Default settings
   */
  static getDefaultSettings() {
    return {
      enabled: true,
      defaultReminders: [
        { method: 'email', minutes: 24 * 60 }, // 24 hours
        { method: 'popup', minutes: 30 } // 30 minutes
      ]
    };
  }

  /**
   * Format reminder time for display
   * @param {number} minutes - Minutes before event
   * @returns {string} Formatted time string
   */
  static formatReminderTime(minutes) {
    if (minutes < 60) {
      return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `${hours} hora${hours !== 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(minutes / 1440);
      return `${days} día${days !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Validate reminder settings
   * @param {Array} reminders - Array of reminder objects
   * @returns {boolean} True if valid
   */
  static validateReminders(reminders) {
    if (!Array.isArray(reminders)) {
      return false;
    }

    return reminders.every(reminder => {
      return (
        reminder &&
        typeof reminder === 'object' &&
        ['email', 'popup', 'sms'].includes(reminder.method) &&
        typeof reminder.minutes === 'number' &&
        reminder.minutes >= 0 &&
        reminder.minutes <= 40320 // Max 4 weeks
      );
    });
  }
}

export default GoogleCalendarService;
